import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  Users,
  MessageCircle,
  Send,
  Lock,
  Unlock,
  Circle,
  X
} from 'lucide-react';

const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

/**
 * Collaboration Presence Component
 * 
 * Shows who's currently viewing/editing a machine and enables real-time collaboration.
 * Features:
 * - User avatars with presence indicators
 * - Cursor sharing (optional)
 * - Edit locking
 * - Chat/activity feed
 */

const CollaborationPresence = ({ 
  machineId, 
  isEnabled = true,
  onCursorUpdate,
  onSelectionUpdate,
  onMachineChange
}) => {
  const [connected, setConnected] = useState(false);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [lockHolder, setLockHolder] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const connect = useCallback(() => {
    if (!machineId || !isEnabled || !WS_URL) return;

    const ws = new WebSocket(`${WS_URL}/ws/collaborate/${machineId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      console.log('Collaboration connected');
    };

    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      handleMessage(message);
    };

    ws.onclose = () => {
      setConnected(false);
      // Attempt to reconnect after 3 seconds
      reconnectTimeoutRef.current = setTimeout(connect, 3000);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  }, [machineId, isEnabled]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
    setUsers([]);
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  const handleMessage = (message) => {
    switch (message.type) {
      case 'init':
        setUsers(message.payload.users || []);
        setActivity(message.payload.activity || []);
        setLockHolder(message.payload.lock_holder);
        break;
      
      case 'presence':
        setUsers(message.payload.all_users || []);
        if (message.payload.lock_holder !== undefined) {
          setLockHolder(message.payload.lock_holder);
        }
        break;
      
      case 'cursor':
        onCursorUpdate?.(message.payload);
        break;
      
      case 'selection':
        onSelectionUpdate?.(message.payload);
        break;
      
      case 'change':
        onMachineChange?.(message.payload);
        break;
      
      case 'lock':
        setLockHolder(message.payload.action === 'acquired' ? message.payload.user_id : null);
        break;
      
      case 'chat':
        setActivity(prev => [...prev.slice(-49), {
          type: 'chat',
          user_name: message.payload.user_name,
          message: message.payload.message,
          timestamp: message.timestamp
        }]);
        break;
      
      default:
        break;
    }
  };

  const sendMessage = (type, payload) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, payload }));
    }
  };

  const handleSendChat = () => {
    if (chatMessage.trim()) {
      sendMessage('chat', { message: chatMessage });
      setChatMessage('');
    }
  };

  const requestLock = () => {
    sendMessage('request_lock', {});
  };

  const releaseLock = () => {
    sendMessage('release_lock', {});
  };

  // Cursor tracking (debounced)
  const updateCursor = useCallback((x, y) => {
    sendMessage('cursor', { x, y });
  }, []);

  // Selection tracking
  const updateSelection = useCallback((stateId) => {
    sendMessage('selection', { state_id: stateId });
  }, []);

  // Broadcast machine change
  const broadcastChange = useCallback((description, data) => {
    sendMessage('change', { description, data });
  }, []);

  if (!isEnabled || !machineId) {
    return null;
  }

  const otherUsers = users.filter(u => u.is_editing || users.length <= 5);
  const hasMoreUsers = users.length > 5;

  return (
    <div className="flex items-center space-x-2" data-testid="collaboration-presence">
      {/* Connection Status */}
      <div className={cn(
        "w-2 h-2 rounded-full",
        connected ? "bg-green-500" : "bg-red-500"
      )} />

      {/* User Avatars */}
      <TooltipProvider>
        <div className="flex -space-x-2">
          {otherUsers.slice(0, 5).map((user, i) => (
            <Tooltip key={user.user_id}>
              <TooltipTrigger asChild>
                <div 
                  className={cn(
                    "relative w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold cursor-pointer transition-transform hover:scale-110",
                    user.is_editing 
                      ? "border-amber-400 bg-amber-500/30" 
                      : "border-white/20 bg-gradient-to-br from-blue-500 to-purple-500"
                  )}
                  style={{ zIndex: 10 - i }}
                >
                  {user.user_avatar ? (
                    <img 
                      src={user.user_avatar} 
                      alt={user.user_name}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    user.user_name?.charAt(0).toUpperCase() || '?'
                  )}
                  
                  {/* Editing indicator */}
                  {user.is_editing && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full flex items-center justify-center">
                      <Lock className="w-2 h-2 text-black" />
                    </div>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{user.user_name}</p>
                {user.is_editing && <p className="text-amber-400 text-xs">Editing</p>}
                {user.selected_state && <p className="text-xs text-gray-400">Viewing: {user.selected_state}</p>}
              </TooltipContent>
            </Tooltip>
          ))}
          
          {hasMoreUsers && (
            <div className="w-7 h-7 rounded-full border-2 border-white/20 bg-gray-700 flex items-center justify-center text-xs">
              +{users.length - 5}
            </div>
          )}
        </div>
      </TooltipProvider>

      {/* User Count */}
      <Badge variant="outline" className="text-[10px] h-5">
        <Users className="w-3 h-3 mr-1" />
        {users.length}
      </Badge>

      {/* Lock Controls */}
      {lockHolder ? (
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs text-amber-400"
          onClick={releaseLock}
          disabled={!users.some(u => u.user_id === lockHolder && u.is_editing)}
        >
          <Lock className="w-3 h-3 mr-1" />
          Locked
        </Button>
      ) : (
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs"
          onClick={requestLock}
        >
          <Unlock className="w-3 h-3 mr-1" />
          Edit
        </Button>
      )}

      {/* Chat Toggle */}
      <Button
        size="sm"
        variant="ghost"
        className={cn("h-6 w-6 p-0", chatOpen && "bg-white/10")}
        onClick={() => setChatOpen(!chatOpen)}
      >
        <MessageCircle className="w-3 h-3" />
      </Button>

      {/* Chat Panel */}
      {chatOpen && (
        <div className="absolute top-full right-0 mt-2 w-80 bg-[#0a0e27] border border-white/10 rounded-lg shadow-xl z-50">
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
            <span className="text-sm font-medium">Team Chat</span>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0"
              onClick={() => setChatOpen(false)}
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
          
          <ScrollArea className="h-64 p-3">
            {activity.filter(a => a.type === 'chat').map((msg, i) => (
              <div key={i} className="mb-2">
                <div className="flex items-baseline space-x-2">
                  <span className="text-xs font-medium text-blue-400">{msg.user_name}</span>
                  <span className="text-[10px] text-gray-600">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <p className="text-sm text-gray-300">{msg.message}</p>
              </div>
            ))}
          </ScrollArea>
          
          <div className="flex items-center space-x-2 p-3 border-t border-white/10">
            <Input
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
              placeholder="Type a message..."
              className="h-8 text-sm bg-black/30 border-white/10"
              onKeyDown={(e) => e.key === 'Enter' && handleSendChat()}
            />
            <Button
              size="sm"
              className="h-8 w-8 p-0"
              onClick={handleSendChat}
            >
              <Send className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollaborationPresence;
